import {Component, OnInit, OnDestroy, ElementRef} from "@angular/core";
import {Observable} from "rxjs";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import * as Communication from "../communication/index";
import {Router, ActivatedRoute} from "@angular/router";
import {Config} from "../../environments/environment";
import {NavigationService} from "../shared/common/NavigationEventService";
import {NavigationEvent} from "../shared/common/NavigationEvent";
import { Subscription } from 'rxjs';
import {Location} from "@angular/common";
import {ToolbarService} from "../shared/common/ToolbarEventService";
import {NotificationService} from "../shared/common/NotificationService";
import {ToolbarEventPageEnum, ToolbarEvent} from "../shared/common/ToolbarEvent";
import {Authorization} from "../shared/authorization/authorization.component";
import {TranslateService} from '@ngx-translate/core';
import {formatDayMonthYear} from "../shared/common/date-format";

declare var $:any;
declare var swal: any;

/**
 * This class represents the lazy loaded AccountComponent.
 */
@Component({
    selector: 'sd-subscription',
    templateUrl: 'subscription.component.html',
    styleUrls: ['subscription.component.css'],
    providers: [Communication.JobsService, Communication.DocumentsService, Communication.RecipientRequestService, Communication.PlansService, Communication.SubscriptionService],
    standalone: false
})
export class SubscriptionComponent implements OnInit, OnDestroy {

  subscriptions:Subscription[] = [];

  paymentAction:string="";
  paymentModalVisibility:boolean=false;
  planUpgradePrice:string="";
  planUpgradeCurrency:string="";
  planRenewalDate:string="";
  userSubscription: any = null;
  paymentMethod:string = Config.PAYMENT_METHOD;

  /** Live per-call gate for the deletion entry point. Never re-derived from plan or status. */
  deleteAccountAvailable:boolean = false;
  /** Pending-deletion state of the account, from the same payload as the gate. */
  accountDeletion: Communication.DeletionState = null;
  userEmail:string = "";
  actionInProgress:boolean = false;

  constructor(private jobsApi:Communication.JobsService,
              private documentsApi:Communication.DocumentsService,
              private usersApi:Communication.UsersService,
              private plansApi:Communication.PlansService,
              private subscriptionsApi:Communication.SubscriptionService,
              private http:HttpClient,
              private activatedRoute:ActivatedRoute,
              private router:Router,
              private navigationService:NavigationService,
              private toolbarService: ToolbarService,
              private location:Location,
              public authorization: Authorization,
              private notificationService: NotificationService,
              public translate: TranslateService)
  {






    this.userSubscription = this.authorization.userAccount.subscription;
  }

  ngOnInit() {
    if (!this.authorization.authenticated()) {
      this.router.navigate(['/']);
    } else {

      this.toolbarService.action(new ToolbarEvent(ToolbarEventPageEnum.SUBSCRIPTION, "", null, "", "", "", "", "", ""));

      this.subscriptions.push(this.activatedRoute.params.subscribe(
        (param) => {
          //convert string to number with +
          //let routeParam = +param['subscription'];
        }));

      this.getPlanPrice();
      this.getExpirationDate();
      this.reloadAccount();
    }

  }

  getExpirationDate(){
    this.planRenewalDate = this.userSubscription.planExpiryDate;
  }

  /**
   * Re-reads the account and re-renders the subscription and deletion panels from that one
   * payload, so the two can never show halves of different states.
   */
  reloadAccount(){
    this.subscriptions.push(this.authorization.refreshAccount().subscribe(
      (user:Communication.UserInfo) => {
        this.userSubscription = user.subscription;
        this.deleteAccountAvailable = user.deleteAccountAvailable === true;
        this.accountDeletion = user.accountDeletion;
        this.userEmail = user.email;
        this.getExpirationDate();
        this.actionInProgress = false;
      },
      (err:any) => {
        this.actionInProgress = false;
        console.log(err);
        this.notificationService.displayError(err);
      }
    ));
  }

  get isPaidPlan():boolean {
    let planName = this.userSubscription?.plan?.name;
    return !!planName && planName !== 'FREE' && planName !== 'TRIAL';
  }

  get isCancelPending():boolean {
    return this.userSubscription?.cancelAtPeriodEnd === true;
  }

  get isPendingDeletion():boolean {
    return this.accountDeletion?.pendingDeletion === true;
  }

  /** The date access ends — the same value the confirmation mail states. */
  get effectiveEndDate():string {
    return this.userSubscription?.planExpiryDate;
  }

  cancelSubscription(){
    if (this.actionInProgress) { return; }

    let planName = this.translate.instant(this.userSubscription?.plan?.name);
    this.notificationService.showCancelSubscriptionAlert(planName, this.formatDate(this.effectiveEndDate), (isConfirmed:boolean) => {
      if (!isConfirmed) { return; }

      this.actionInProgress = true;
      this.subscriptions.push(this.subscriptionsApi.subscriptionsCancelPost(this.authorization.getAccessToken()).subscribe(
        (response:Communication.CancelResult) => {
          this.notificationService.subscriptionCancelled(this.formatDate(response.effectiveEndDate || response.planExpiryDate));
          this.reloadAccount();
        },
        (err:any) => {
          this.actionInProgress = false;
          console.log(err);
          this.notificationService.displayError(err);
        }
      ));
    });
  }

  resumeSubscription(){
    if (this.actionInProgress) { return; }

    this.notificationService.showResumeSubscriptionAlert((isConfirmed:boolean) => {
      if (!isConfirmed) { return; }

      this.actionInProgress = true;
      this.subscriptions.push(this.subscriptionsApi.subscriptionsResumePost(this.authorization.getAccessToken()).subscribe(
        () => {
          this.notificationService.subscriptionResumed();
          // Resuming also clears a pending deletion, so both panels are re-read together.
          this.reloadAccount();
        },
        (err:any) => {
          this.actionInProgress = false;
          console.log(err);
          this.notificationService.displayError(err);
        }
      ));
    });
  }

  deleteAccount(){
    if (this.actionInProgress || !this.deleteAccountAvailable) { return; }

    this.notificationService.showDeleteAccountConfirm((isConfirmed:boolean) => {
      if (!isConfirmed) { return; }

      this.actionInProgress = true;
      this.subscriptions.push(this.usersApi.usersDeletionRequestPost(this.authorization.getAccessToken()).subscribe(
        () => {
          this.actionInProgress = false;
          this.notificationService.deleteAccountLinkSent(this.userEmail);
        },
        (err:any) => {
          this.actionInProgress = false;
          console.log(err);
          this.notificationService.displayError(err);
        }
      ));
    });
  }

  abortDeletion(){
    if (this.actionInProgress) { return; }

    this.notificationService.showAbortDeletionAlert((isConfirmed:boolean) => {
      if (!isConfirmed) { return; }

      this.actionInProgress = true;
      this.subscriptions.push(this.usersApi.usersDeletionRequestDelete(this.authorization.getAccessToken()).subscribe(
        () => {
          this.notificationService.deletionRequestAborted();
          this.reloadAccount();
        },
        (err:any) => {
          this.actionInProgress = false;
          console.log(err);
          this.notificationService.displayError(err);
        }
      ));
    });
  }

  /**
   * The template calls this too, rather than applying a date pipe itself, so the end date in a
   * dialog and the end date on the page cannot drift into different formats.
   */
  formatDate(value:any):string {
    return formatDayMonthYear(value);
  }


  getPlanPrice(){

    this.subscriptions.push(this.plansApi.userPlansIdPeriodGet(this.authorization.getAccessToken(), this.userSubscription.plan.name, this.userSubscription.period).subscribe(
      (response:Communication.PlanPrice) => {
        this.planUpgradePrice = response.currentPrice;
        this.planUpgradeCurrency = this.userSubscription.plan.priceCurrency;

        if(this.userSubscription.coupon != null && this.userSubscription.coupon.code != ""){
          this.subscriptionsApi.couponsGet(this.authorization.getAccessToken(),String(this.userSubscription.coupon.code)).subscribe(
            (response:any) => {
              let dayTime:number = 24*60*60*1000;

              //whether couponCode is valid or not
              if(Number(response.usageExpiry) > Date.now() && (
                 Number(response.discountExpiryDate) >= Number(this.userSubscription.planExpiryDate) ||
                 Number(this.userSubscription.couponStartDate) + Number(response.discountExpiryPeriod) * dayTime >= Number(this.userSubscription.planExpiryDate)))
              {
                this.planUpgradePrice = ((1 - response.discount / 100) * Number(this.planUpgradePrice)).toFixed(2);
              }

            }, //Bind to view
            (err:any) => {
              // Log errors if any
              console.log(err);
              this.notificationService.displayError(err);
            });

        }

      }, //Bind to view
      (err:any) => {
        // Log errors if any
        console.log(err);
        this.notificationService.displayError(err);
      }
    ));
  }


  changePlan(){
    this.router.navigate(['/account/upgrade']);
  }

  updateCardInvoiceInfo(){
    this.paymentModalVisibility = true;
    this.paymentAction = "update";
  }

  openStripePortal(){
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + this.authorization.getAccessToken());
    this.http.get(Config.WEBSERVICEURL + '/subscriptions/stripe/portal', { headers: headers }).subscribe(
      (response:any) => {
        window.open(response.response.url, '_blank');
      },
      (error:any) => {
        console.log(error);
        this.notificationService.displayError(error);
      }
    );
  }

  ngOnDestroy() {
     $('.modal-backdrop').remove();

    this.subscriptions.forEach(
      (s:Subscription) => s.unsubscribe()
    );
  }
}
